import React, { useState, useEffect } from 'react';
import { observable } from 'mobx';
import { createViewModel } from 'mobx-utils';
import { observer, inject } from 'mobx-react';

import { Label, Dropdown, DefaultButton, ResponsiveMode, Checkbox } from '@fluentui/react';
import { FontSizes, FontWeights, mergeStyleSets } from '@fluentui/react';

import CheckboxWithTooltip from 'src/components/base/CheckboxWithTooltip';
import { runShortAction } from 'src/services/utils';

const LogoutTimeouts = [
  {
    text: '1 hour',
    key: '1h',
  },
  {
    text: '6 hours',
    key: '6h',
  },
  {
    text: '12 hours',
    key: '12h',
  },
  {
    text: 'Indefinitely',
    key: '1y',
  },
];

const classNames = mergeStyleSets({
  root: {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  block: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    marginBottom: 26,
    selectors: {
      '& > *': {
        marginBottom: 16,
      }
    },
  },
  title: {
    fontSize: FontSizes.large,
    fontWeight: FontWeights.semilight,
    marginBottom: 16,
  },
})

const Settings = ({ userStore, goBack }) => {
  const [model, setModel] = useState({ isDirty: false });
  const [timezones, setTimezones] = useState([]);

  useEffect(() => {
    const settings = { logoutTimeout: '1y', enableManualOperationsLog: true, debugMode: false, ...userStore.settings };

    initializeTimezones(settings.timezone)
      .then(({ timezone, timezones }) => {
        setTimezones(timezones)
        setModel(createViewModel(observable({ ...settings, timezone })));
      });
  }, [userStore.settings]);

  function handleModelChange(value, index) {
    model[index] = value;
  }

  async function handleSaveClick() {
    await runShortAction(async () => {
      const data = {
        timezone: model.timezone,
        logoutTimeout: model.logoutTimeout,
        enableManualOperationsLog: model.enableManualOperationsLog,
      };
      if (userStore.settings.superSettings) {
        data.debugMode = model.debugMode;
      }

      await userStore.updateSettings(data);
      goBack();
    })
  }

  return (
    <div className={classNames.root}>
      <div className={classNames.block}>
        <Label className={classNames.title}>General</Label>

        <Dropdown
          label='Preferred Time Zone'
          selectedKey={model.timezone}
          onChange={(event, item) => handleModelChange(item.key, 'timezone')}
          responsiveMode={ResponsiveMode.large}
          disabled={!timezones.length}
          options={timezones.map(timezone => ({ key: timezone.key, text: timezone.text }))}
        />

        <Dropdown
          label='Keep me logged in for:'
          selectedKey={model.logoutTimeout}
          onChange={(event, item) => handleModelChange(item.key, 'logoutTimeout')}
          responsiveMode={ResponsiveMode.large}
          options={LogoutTimeouts}
        />

        <CheckboxWithTooltip
          label={'Enable manual operations log'}
          tooltip={'Creates a new worksheet called ‘Manual Operations Events Log’ and keeps a log of all manual operations against Salesforce there'}
          checked={model.enableManualOperationsLog ?? false}
          onChange={(e, isChecked) => handleModelChange(isChecked, 'enableManualOperationsLog')}
        />

        {userStore.settings.superSettings && (
          <Checkbox
            label={'Debug Mode'}
            checked={model.debugMode ?? false}
            onChange={(e, isChecked) => handleModelChange(isChecked, 'debugMode')}
          />
        )}

      </div>

      <div className={classNames.block}>
        <DefaultButton primary={true} text='Save' disabled={!model.isDirty} onClick={handleSaveClick} />
      </div>
    </div>
  )
}

async function initializeTimezones(timezone) {
  try {
    const { getTimezones, guessUserTimezone } = await import(/* webpackChunkName: "timezone" */ 'src/services/timezone');
    const timezones = getTimezones(timezone)
      .map(timezone => ({
        key: timezone.timezone,
        text: `GMT${timezone.offsetAsString} ${timezone.timezone} ${timezone.abbreviation ? '(' + timezone.abbreviation + ')' : ''}`,
      }));

    return { timezones, timezone: timezone ?? guessUserTimezone() ?? '' }
  }
  catch (error) { console.error(error) }
}

export default inject('userStore')(observer(Settings));
